// src/pages/nascar/CupArticle.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { MDXProvider } from "@mdx-js/react";
import { evaluate } from "@mdx-js/mdx";
import * as runtime from "react/jsx-runtime";

import { supabase } from "../../supabaseClient";
import { NASCAR_ALL } from "../../auth/plans";

export default function CupArticle() {
  const { slug } = useParams();
  const location = useLocation();

  const articleUrl = `/data/nascar/cup/articles/${slug}/article.mdx`;
  const metaUrl = `/data/nascar/cup/articles/${slug}/meta.json`;

  const [MdxContent, setMdxContent] = useState(null);
  const [meta, setMeta] = useState(null);

  const [metaLoading, setMetaLoading] = useState(true);
  const [accessLoading, setAccessLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);

  const [articleFound, setArticleFound] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [scrollProgress, setScrollProgress] = useState(0);

  // Local light/dark theme for article pages.
  const [theme, setTheme] = useState("dark");
  const isLight = theme === "light";

  /*
    Custom components available inside article.mdx.
  */
  const mdxComponents = useMemo(
    () => ({
      h1: (props) => (
        <h1
          className="mb-4 mt-8 text-2xl font-bold tracking-tight"
          {...props}
        />
      ),

      h2: (props) => (
        <h2
          className="mb-3 mt-8 text-xl font-semibold tracking-tight"
          {...props}
        />
      ),

      h3: (props) => (
        <h3
          className="mb-2 mt-6 text-lg font-semibold"
          {...props}
        />
      ),

      p: (props) => (
        <p
          className="mb-4 leading-7"
          {...props}
        />
      ),

      ul: (props) => (
        <ul
          className="mb-4 list-disc space-y-1 pl-6"
          {...props}
        />
      ),

      ol: (props) => (
        <ol
          className="mb-4 list-decimal space-y-1 pl-6"
          {...props}
        />
      ),

      a: (props) => (
        <a
          className="font-medium text-blue-500 underline decoration-blue-500/60 hover:text-blue-400"
          {...props}
        />
      ),

      strong: (props) => (
        <strong
          className="font-semibold"
          {...props}
        />
      ),

      pre: (props) => (
        <pre
          className="mb-4 overflow-x-auto rounded-xl bg-neutral-950/95 p-4 text-xs text-neutral-50 sm:text-sm"
          {...props}
        />
      ),

      code: (props) => (
        <code
          className="rounded bg-neutral-800 px-1.5 py-0.5 text-[0.85em]"
          {...props}
        />
      ),

      table: (props) => (
        <div className="my-6 overflow-x-auto rounded-2xl border border-neutral-700 bg-neutral-900/80">
          <table
            className="min-w-full divide-y divide-neutral-800 text-sm"
            {...props}
          />
        </div>
      ),

      th: (props) => (
        <th
          className="bg-neutral-900 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-300"
          {...props}
        />
      ),

      td: (props) => (
        <td
          className="px-3 py-2 align-top text-sm text-neutral-200"
          {...props}
        />
      ),

      img: (props) => (
        <img
          className="mx-auto my-6 max-h-[420px] w-auto rounded-xl border border-neutral-800"
          loading="lazy"
          {...props}
        />
      ),

      Callout: ({ type = "info", children }) => {
        const colors =
          type === "warning"
            ? "border-amber-500/60 bg-amber-500/10 text-amber-100"
            : type === "danger"
              ? "border-red-500/60 bg-red-500/10 text-red-100"
              : "border-blue-500/60 bg-blue-500/10 text-blue-100";

        return (
          <div
            className={`mb-4 rounded-2xl border px-4 py-3 text-sm ${colors}`}
          >
            {children}
          </div>
        );
      },
    }),
    []
  );

  /*
    Load only meta.json first.

    This allows the title, subtitle, tags and race snapshot to remain
    visible before the full article body is unlocked.
  */
  useEffect(() => {
    let alive = true;

    async function loadMeta() {
      try {
        setMetaLoading(true);
        setAccessLoading(true);
        setContentLoading(false);

        setArticleFound(true);
        setMeta(null);
        setMdxContent(null);

        setHasAccess(false);
        setIsLoggedIn(false);

        const response = await fetch(metaUrl, {
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error("Article metadata not found");
        }

        const metaJson = await response.json();

        if (!alive) return;

        setMeta(metaJson);
      } catch (error) {
        console.error(error);

        if (!alive) return;

        setArticleFound(false);
        setMeta(null);
        setHasAccess(false);
      } finally {
        if (alive) {
          setMetaLoading(false);
        }
      }
    }

    loadMeta();

    return () => {
      alive = false;
    };
  }, [metaUrl]);

  /*
    Determine whether the visitor may load the article body.
  */
  useEffect(() => {
    let alive = true;

    async function checkAccess() {
      if (metaLoading) return;

      if (!articleFound || !meta) {
        if (alive) {
          setHasAccess(false);
          setAccessLoading(false);
        }

        return;
      }

      try {
        setAccessLoading(true);

        // Free article: no account or plan required.
        if (meta.free === true) {
          if (!alive) return;

          setHasAccess(true);
          setIsLoggedIn(false);
          setAccessLoading(false);

          return;
        }

        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!alive) return;

        if (!user) {
          setIsLoggedIn(false);
          setHasAccess(false);
          setAccessLoading(false);

          return;
        }

        setIsLoggedIn(true);

        const { data: profile, error } = await supabase
          .from("profiles")
          .select("plan,status")
          .eq("id", user.id)
          .single();

        if (!alive) return;

        if (error) {
          console.error(error);
        }

        const active =
          profile?.status === "active" ||
          profile?.status === "trialing";

        const validNascarPlan = NASCAR_ALL.includes(profile?.plan);

        setHasAccess(Boolean(active && validNascarPlan));
      } catch (error) {
        console.error(error);

        if (alive) {
          setHasAccess(false);
        }
      } finally {
        if (alive) {
          setAccessLoading(false);
        }
      }
    }

    checkAccess();

    return () => {
      alive = false;
    };
  }, [articleFound, meta, metaLoading]);

  /*
    Load article.mdx only after access is approved.
  */
  useEffect(() => {
    let alive = true;

    async function loadArticleBody() {
      if (accessLoading || !hasAccess || !articleFound) {
        setMdxContent(null);
        return;
      }

      try {
        setContentLoading(true);

        const response = await fetch(articleUrl, {
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error("Article body not found");
        }

        const mdxText = await response.text();

        const mdxModule = await evaluate(mdxText, {
          ...runtime,
          useMDXComponents: () => mdxComponents,
        });

        if (!alive) return;

        setMdxContent(() => mdxModule.default);
      } catch (error) {
        console.error(error);

        if (!alive) return;

        setMdxContent(() => () => (
          <div>
            <h2 className="mb-2 text-xl font-semibold">
              Article unavailable
            </h2>

            <p className="text-sm text-neutral-300">
              We couldn&apos;t load this breakdown.
            </p>
          </div>
        ));
      } finally {
        if (alive) {
          setContentLoading(false);
        }
      }
    }

    loadArticleBody();

    return () => {
      alive = false;
    };
  }, [
    accessLoading,
    articleFound,
    articleUrl,
    hasAccess,
    mdxComponents,
  ]);

  /*
    Update the browser title, description and canonical URL.

    This does not alter the visible article layout.
  */
  useEffect(() => {
    if (!meta) return undefined;

    const oldTitle = document.title;

    const titleText = `${meta.title} | CPenn DFS`;

    const descriptionText =
      meta.subtitle ||
      "NASCAR Cup Series DFS strategy and betting breakdown.";

    document.title = titleText;

    let description = document.querySelector(
      'meta[name="description"]'
    );

    const previousDescription =
      description?.getAttribute("content") ?? null;

    const descriptionWasCreated = !description;

    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    description.setAttribute("content", descriptionText);

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    );

    const previousCanonical =
      canonical?.getAttribute("href") ?? null;

    const canonicalWasCreated = !canonical;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      "href",
      `https://www.cpenn-dfs.com/nascar/cup/articles/${slug}`
    );

    return () => {
      document.title = oldTitle;

      if (descriptionWasCreated) {
        description?.remove();
      } else if (description && previousDescription !== null) {
        description.setAttribute(
          "content",
          previousDescription
        );
      }

      if (canonicalWasCreated) {
        canonical?.remove();
      } else if (canonical && previousCanonical !== null) {
        canonical.setAttribute(
          "href",
          previousCanonical
        );
      }
    };
  }, [meta, slug]);

  /*
    Article reading progress.
  */
  useEffect(() => {
    function handleScroll() {
      const documentElement = document.documentElement;

      const total =
        documentElement.scrollHeight -
        documentElement.clientHeight;

      if (total <= 0) {
        setScrollProgress(0);
        return;
      }

      const current = window.scrollY;

      const percentage = Math.min(
        100,
        Math.max(0, (current / total) * 100)
      );

      setScrollProgress(percentage);
    }

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const title = meta?.title || "Cup Series Article";

  const subtitle =
    meta?.subtitle ||
    "NASCAR Cup Series race breakdown and DFS/betting notes.";

  const publishedDate = useMemo(() => {
    if (!meta?.published_at) return null;

    const date = new Date(
      `${meta.published_at}T12:00:00`
    );

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, [meta]);

  const readingTime = meta?.read_time || null;

  const tags =
    Array.isArray(meta?.tags) && meta.tags.length > 0
      ? meta.tags
      : ["NASCAR Cup", "Race Breakdown"];

  const raceMeta = {
    race: meta?.race || meta?.Race,

    track: meta?.track || meta?.Track,

    laps: meta?.laps || meta?.Laps,

    dominatorPoints:
      meta?.dominator_points ||
      meta?.dominatorPoints ||
      meta?.DominatorPoints,

    similarTracks:
      meta?.similar_tracks ||
      meta?.similarTracks ||
      meta?.SimilarTracks,
  };

  const isFree = meta?.free === true;

  const pageBgClass = isLight
    ? "bg-slate-50 text-slate-900"
    : "bg-neutral-950 text-neutral-50";

  const articleCardClass = isLight
    ? "border-slate-200 bg-white shadow-[0_20px_70px_rgba(15,23,42,0.2)]"
    : "border-neutral-700 bg-neutral-900/70 shadow-[0_28px_80px_rgba(0,0,0,0.7)]";

  const snapshotCardClass = isLight
    ? "border-slate-200 bg-white shadow-[0_16px_50px_rgba(15,23,42,0.18)]"
    : "border-neutral-800 bg-gradient-to-br from-neutral-900/90 via-neutral-900/95 to-neutral-950/95 shadow-[0_18px_45px_rgba(0,0,0,0.85)]";

  if (metaLoading) {
    return (
      <div className={`min-h-screen ${pageBgClass}`}>
        <div className="mx-auto w-full max-w-5xl px-4 pb-20 pt-12">
          <p
            className={
              isLight
                ? "text-slate-600"
                : "text-neutral-300"
            }
          >
            Loading article…
          </p>
        </div>
      </div>
    );
  }

  if (!articleFound) {
    return (
      <div className={`min-h-screen ${pageBgClass}`}>
        <div className="mx-auto w-full max-w-5xl px-4 pb-20 pt-12">
          <Link
            to="/nascar/cup/articles"
            className="text-sm font-medium text-blue-500 hover:text-blue-400"
          >
            ← Back to Articles
          </Link>

          <div
            className={
              "mt-8 rounded-3xl border p-8 " +
              (isLight
                ? "border-slate-200 bg-white"
                : "border-neutral-800 bg-neutral-900/70")
            }
          >
            <h1 className="text-2xl font-bold">
              Article not found
            </h1>

            <p
              className={
                "mt-2 text-sm " +
                (isLight
                  ? "text-slate-600"
                  : "text-neutral-300")
              }
            >
              We couldn&apos;t find this NASCAR Cup article.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${pageBgClass}`}
    >
      {!isLight && (
        <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_55%),radial-gradient(circle_at_bottom,_rgba(56,189,248,0.16),_transparent_60%]" />
      )}

      <div className="mx-auto w-full max-w-5xl px-4 pb-24 pt-10">
        {/* Back link and theme toggle */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <Link
            to="/nascar/cup/articles"
            className="inline-flex items-center text-sm font-medium text-blue-500 hover:text-blue-400"
          >
            ← Back to Articles
          </Link>

          <button
            type="button"
            onClick={() =>
              setTheme((previous) =>
                previous === "dark" ? "light" : "dark"
              )
            }
            className="inline-flex items-center gap-2 rounded-full border border-neutral-700/50 bg-neutral-900/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-200 shadow-sm transition hover:border-blue-500/70 hover:text-blue-100 hover:shadow-[0_0_20px_rgba(59,130,246,0.45)] sm:text-[0.7rem]"
          >
            <span
              className={`text-base ${
                isLight
                  ? "text-amber-400"
                  : "text-blue-300"
              }`}
            >
              {isLight ? "☀" : "☾"}
            </span>

            <span>
              {isLight ? "Light mode" : "Dark mode"}
            </span>
          </button>
        </div>

        {/* Public article header */}
        <header className="mb-10 space-y-6">
          <div
            className={`inline-flex flex-wrap items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide shadow-sm ${
              isLight
                ? "border border-blue-300 bg-blue-100 text-blue-700"
                : "border border-blue-500/40 bg-blue-500/10 text-blue-200 shadow-[0_0_25px_rgba(37,99,235,0.45)]"
            }`}
          >
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-[0.6rem] font-extrabold text-white">
              C
            </span>

            <span>NASCAR Cup</span>

            <span className="text-neutral-400">
              •
            </span>

            <span>Race Preview</span>
          </div>

          <div className="grid gap-8 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)] md:items-start">
            <div className="space-y-4">
              <h1 className="text-balance text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl">
                {title}
              </h1>

              <p
                className={
                  "max-w-2xl text-sm sm:text-base " +
                  (isLight
                    ? "text-slate-600"
                    : "text-neutral-400")
                }
              >
                {subtitle}
              </p>

              <div
                className={
                  "flex flex-wrap items-center gap-3 text-xs sm:text-sm " +
                  (isLight
                    ? "text-slate-500"
                    : "text-neutral-400")
                }
              >
                {readingTime && (
                  <span>{readingTime}</span>
                )}

                {isFree && (
                  <>
                    <span className="h-1 w-1 rounded-full bg-neutral-600" />

                    <span
                      className={
                        "inline-flex items-center rounded-full px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-wide ring-1 " +
                        (isLight
                          ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
                          : "bg-emerald-500/15 text-emerald-300 ring-emerald-500/40")
                      }
                    >
                      FREE
                    </span>
                  </>
                )}

                {publishedDate && (
                  <>
                    <span className="h-1 w-1 rounded-full bg-neutral-600" />
                    <span>{publishedDate}</span>
                  </>
                )}

                <span className="h-1 w-1 rounded-full bg-neutral-600" />

                <div className="flex flex-wrap gap-1">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[0.7rem] font-semibold ${
                        isLight
                          ? "bg-blue-100 text-blue-700 ring-1 ring-blue-300"
                          : "bg-blue-500/10 text-blue-200 ring-1 ring-blue-500/40"
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Race snapshot */}
            <aside
              className={`rounded-3xl border p-4 md:p-5 ${snapshotCardClass}`}
            >
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-400">
                Race Snapshot
              </h2>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {raceMeta.race && (
                  <div
                    className={
                      "rounded-2xl px-3 py-2 " +
                      (isLight
                        ? "bg-slate-100"
                        : "bg-neutral-900/70")
                    }
                  >
                    <p className="text-[0.7rem] uppercase tracking-wide text-neutral-500">
                      Race
                    </p>

                    <p
                      className={
                        "mt-0.5 text-sm font-semibold " +
                        (isLight
                          ? "text-slate-900"
                          : "text-neutral-50")
                      }
                    >
                      {raceMeta.race}
                    </p>
                  </div>
                )}

                {raceMeta.track && (
                  <div
                    className={
                      "rounded-2xl px-3 py-2 " +
                      (isLight
                        ? "bg-slate-100"
                        : "bg-neutral-900/70")
                    }
                  >
                    <p className="text-[0.7rem] uppercase tracking-wide text-neutral-500">
                      Track
                    </p>

                    <p
                      className={
                        "mt-0.5 text-sm font-semibold " +
                        (isLight
                          ? "text-slate-900"
                          : "text-neutral-50")
                      }
                    >
                      {raceMeta.track}
                    </p>
                  </div>
                )}

                {raceMeta.laps && (
                  <div
                    className={
                      "rounded-2xl px-3 py-2 " +
                      (isLight
                        ? "bg-slate-100"
                        : "bg-neutral-900/70")
                    }
                  >
                    <p className="text-[0.7rem] uppercase tracking-wide text-neutral-500">
                      Laps
                    </p>

                    <p className="mt-0.5 text-lg font-bold text-blue-500">
                      {raceMeta.laps}
                    </p>
                  </div>
                )}

                {raceMeta.dominatorPoints && (
                  <div
                    className={
                      "rounded-2xl px-3 py-2 " +
                      (isLight
                        ? "bg-slate-100"
                        : "bg-neutral-900/70")
                    }
                  >
                    <p className="text-[0.7rem] uppercase tracking-wide text-neutral-500">
                      Dom. Pts
                    </p>

                    <p className="mt-0.5 text-lg font-bold text-emerald-500">
                      {raceMeta.dominatorPoints}
                    </p>
                  </div>
                )}

                {raceMeta.similarTracks && (
                  <div
                    className={
                      "col-span-2 rounded-2xl px-3 py-2 " +
                      (isLight
                        ? "bg-slate-100"
                        : "bg-neutral-900/70")
                    }
                  >
                    <p className="text-[0.7rem] uppercase tracking-wide text-neutral-500">
                      Similar Tracks
                    </p>

                    <p
                      className={
                        "mt-0.5 text-sm font-medium " +
                        (isLight
                          ? "text-slate-900"
                          : "text-neutral-50")
                      }
                    >
                      {raceMeta.similarTracks}
                    </p>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </header>

        {/* Progress bar only appears for unlocked articles */}
        {hasAccess && !accessLoading && (
          <div className="sticky top-16 z-30 mb-6">
            <div className="flex items-center gap-3 rounded-2xl border border-neutral-800/70 bg-neutral-950/80 px-4 py-2 text-xs text-neutral-300 shadow-[0_12px_40px_rgba(0,0,0,0.75)] backdrop-blur">
              <div className="flex min-w-0 items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[0.7rem] font-bold text-white">
                  C
                </span>

                <span className="truncate text-[0.75rem] font-semibold uppercase tracking-wide text-neutral-100 sm:text-xs">
                  {title}
                </span>
              </div>

              <div className="flex-1">
                <div className="h-1 rounded-full bg-neutral-800">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 via-sky-400 to-emerald-400 transition-[width] duration-150 ease-out"
                    style={{
                      width: `${scrollProgress}%`,
                    }}
                  />
                </div>
              </div>

              <span className="hidden text-[0.7rem] text-neutral-400 sm:inline">
                {Math.round(scrollProgress)}%
              </span>
            </div>
          </div>
        )}

        {/* Article body or blurred locked placeholder */}
        <div
          className={`rounded-3xl border p-6 sm:p-8 ${articleCardClass}`}
        >
          {accessLoading ? (
            <p
              className={
                "text-sm " +
                (isLight
                  ? "text-slate-600"
                  : "text-neutral-300")
              }
            >
              Checking article access…
            </p>
          ) : hasAccess ? (
            <article
              className={`max-w-none
                prose-headings:scroll-mt-24
                prose-headings:font-semibold
                prose-h2:text-xl
                prose-h3:text-lg
                prose-p:text-neutral-200
                prose-li:text-neutral-200
                prose-strong:text-neutral-100
                prose-h2:text-neutral-50
                prose-h3:text-neutral-100
                prose-p:text-[0.975rem]
                prose-p:leading-relaxed
                prose-a:no-underline
                hover:prose-a:underline
                ${isLight ? "prose prose-slate" : "prose prose-invert"}
              `}
            >
              {contentLoading ? (
                <p
                  className={
                    "text-sm " +
                    (isLight
                      ? "text-slate-600"
                      : "text-neutral-300")
                  }
                >
                  Loading article…
                </p>
              ) : MdxContent ? (
                <MDXProvider components={mdxComponents}>
                  <MdxContent />
                </MDXProvider>
              ) : (
                <p
                  className={
                    "text-sm " +
                    (isLight
                      ? "text-slate-600"
                      : "text-neutral-300")
                  }
                >
                  No article content available.
                </p>
              )}
            </article>
          ) : (
            <div className="relative min-h-[560px] overflow-hidden rounded-2xl">
              {/* Fake article placeholder */}
              <div
                aria-hidden="true"
                className="pointer-events-none select-none opacity-75 blur-[6px]"
              >
                <div className="space-y-8">
                  <section>
                    <div
                      className={
                        "mb-4 h-7 w-52 rounded " +
                        (isLight
                          ? "bg-slate-300"
                          : "bg-neutral-700")
                      }
                    />

                    <div className="space-y-3">
                      <div
                        className={
                          "h-4 w-full rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[97%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[92%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[88%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />
                    </div>
                  </section>

                  <section>
                    <div
                      className={
                        "mb-4 h-7 w-44 rounded " +
                        (isLight
                          ? "bg-slate-300"
                          : "bg-neutral-700")
                      }
                    />

                    <div className="space-y-3">
                      <div
                        className={
                          "h-4 w-full rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[95%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[91%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[84%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />
                    </div>
                  </section>

                  <section>
                    <div
                      className={
                        "mb-4 h-7 w-48 rounded " +
                        (isLight
                          ? "bg-slate-300"
                          : "bg-neutral-700")
                      }
                    />

                    <div className="space-y-3">
                      <div
                        className={
                          "h-4 w-[68%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[74%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[62%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />
                    </div>
                  </section>

                  <section>
                    <div
                      className={
                        "mb-4 h-7 w-40 rounded " +
                        (isLight
                          ? "bg-slate-300"
                          : "bg-neutral-700")
                      }
                    />

                    <div className="space-y-3">
                      <div
                        className={
                          "h-4 w-full rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[96%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[89%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />

                      <div
                        className={
                          "h-4 w-[82%] rounded " +
                          (isLight
                            ? "bg-slate-200"
                            : "bg-neutral-800")
                        }
                      />
                    </div>
                  </section>
                </div>
              </div>

              {/* Fade over fake content */}
              <div
                className={
                  "absolute inset-0 " +
                  (isLight
                    ? "bg-gradient-to-b from-white/10 via-white/70 to-white/95"
                    : "bg-gradient-to-b from-neutral-950/10 via-neutral-950/70 to-neutral-950/95")
                }
              />

              {/* Unlock message */}
              <div className="absolute inset-0 flex items-center justify-center px-4 py-8">
                <div
                  className={
                    "mx-auto max-w-xl rounded-3xl border px-6 py-8 text-center shadow-2xl backdrop-blur-md sm:px-8 " +
                    (isLight
                      ? "border-slate-200 bg-white/90"
                      : "border-neutral-700/80 bg-neutral-950/85")
                  }
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-500/15 text-2xl text-blue-300 ring-1 ring-blue-500/40">
                    🔒
                  </div>

                  <h2
                    className={
                      "mt-4 text-2xl font-bold " +
                      (isLight
                        ? "text-slate-950"
                        : "text-white")
                    }
                  >
                    Unlock the full breakdown
                  </h2>

                  <p
                    className={
                      "mx-auto mt-3 max-w-lg text-sm leading-6 " +
                      (isLight
                        ? "text-slate-600"
                        : "text-neutral-300")
                    }
                  >
                    This NASCAR Cup race breakdown is available to active
                    CPenn DFS NASCAR members. Unlock the full strategy, picks,
                    pivots and betting notes for this race.
                  </p>

                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <Link
                      to={`/pricing?plan=nascar_lite&from=${encodeURIComponent(
                        location.pathname
                      )}`}
                      className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500"
                    >
                      View NASCAR plans
                    </Link>

                    {!isLoggedIn && (
                      <Link
                        to={`/login?from=${encodeURIComponent(
                          location.pathname
                        )}`}
                        className={
                          "rounded-full border px-5 py-2.5 text-sm font-semibold transition " +
                          (isLight
                            ? "border-slate-300 text-slate-800 hover:border-blue-500 hover:text-blue-600"
                            : "border-neutral-700 text-neutral-100 hover:border-blue-500 hover:text-blue-300")
                        }
                      >
                        Log in
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}